import {
  Autocomplete,
  Box,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import type React from 'react';
import { useState } from 'react';
import { listToSelectionRecord } from '../../../../../internal/utils/functions';
import type { ApiReturnImageResource } from './ImageDisplayer';

interface ImageSelectorProps {
  images: ApiReturnImageResource[];
  defaultValue: number | null;
  onSelect: (image: ApiReturnImageResource) => void;
}

function ImageSelector({ images, defaultValue, onSelect }: ImageSelectorProps) {
  const initialSelection = listToSelectionRecord(
    images,
    defaultValue ? [defaultValue] : []
  );
  const [valuesSelection, setValuesSelection] =
    useState<Record<number, boolean>>(initialSelection);
  const [imageSelection, setImageSelection] =
    useState<ApiReturnImageResource[]>(images);
  const [tagFilter, setTagFilter] = useState<string[]>([]);

  const handleTagChange = (_event: unknown, value: string[]) => {
    setTagFilter(value);
    setImageSelection(
      value.length === 0
        ? images
        : images.filter((image) =>
            value.every((tag) => (image.tag_names ?? []).includes(tag))
          )
    );
  };

  const handleImageSelection = (
    _event: React.ChangeEvent<HTMLInputElement>,
    value: string
  ) => {
    const selectedImageId = Number(value);
    const newValuesSelection = listToSelectionRecord(images, [selectedImageId]);
    setValuesSelection(newValuesSelection);
    const selectedImage = images.find((image) => image.id === selectedImageId);
    if (selectedImage) {
      onSelect(selectedImage);
    }
  };

  return (
    <Box>
      <Autocomplete
        multiple
        freeSolo
        options={[]}
        value={tagFilter}
        onChange={handleTagChange}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder="Rechercher par tags"
            size="small"
          />
        )}
        sx={{ mb: 2 }}
      />
      {imageSelection.length !== 0 ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            maxHeight: '50vh',
            overflowY: 'auto',
            p: 2,
          }}
        >
          <RadioGroup
            value={
              Object.entries(valuesSelection).find(
                ([, checked]) => checked
              )?.[0] ?? ''
            }
            onChange={handleImageSelection}
          >
            {imageSelection.map((image) => (
              <Box
                key={image.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  p: 1,
                  cursor: 'pointer',
                  '&:hover': {
                    bgcolor: 'action.hover',
                  },
                }}
                onClick={() =>
                  handleImageSelection(
                    {
                      target: { value: String(image.id) },
                    } as React.ChangeEvent<HTMLInputElement>,
                    String(image.id)
                  )
                }
              >
                <FormControlLabel
                  value={String(image.id)}
                  control={<Radio size="small" />}
                  label=""
                  sx={{ m: 0 }}
                />
                <Box
                  component="img"
                  src={`data:image/png;base64,${image.value}`}
                  alt={image.name}
                  sx={{
                    width: 50,
                    height: 50,
                    objectFit: 'cover',
                    borderRadius: 1,
                    flexShrink: 0,
                  }}
                />
                <Typography variant="body2" noWrap sx={{ flex: 1 }}>
                  {image.name}
                </Typography>
              </Box>
            ))}
          </RadioGroup>
        </Box>
      ) : (
        <Typography align="center" variant="h6">
          Aucune image associée au contexte de ce tableau de bord
        </Typography>
      )}
    </Box>
  );
}

export default ImageSelector;
